import { Spin } from "antd";
import axios from "axios";
import { Edit2, PlusCircle, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DataTable } from "src/components/DataTable";
import { Button } from "src/components/ui/button";
import { useAuth } from "src/context/AuthContext";
import { useLanguage } from "src/context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "src/lib/api";
import DialogDelete from "../../../components/common/DialogDelete";
import MainButton from "../../../components/MainButton";
import FAQDialog from "./FAQDialog";

const FAQPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [FAQData, setFAQData] = useState({});
  const [errors, setErrors] = useState({});

  const [FAQ, setFAQ] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFAQ = async () => {
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/frequently-asked-questions`
      );
      setFAQ(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQ();
  }, []);

  const columns = [
    {
      accessorKey: "question",
      header: "Question",
      cell: ({ row }) => {
        return (
          <div className="flex-1 w-full">{row.original.question || "___"}</div>
        );
      },
    },
    {
      accessorKey: "Actions",
      cell: ({ row }) => {
        const data = row.original;

        return (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setOpen(true);
                setFAQData(data);
              }}
            >
              <Edit2 />
            </Button>
            <DialogDelete
              apiLink={`/frequently-asked-questions/${data?.id}`}
              onSuccess={fetchFAQ}
              itemTitle={data.question}
              name={"FAQ"}
            >
              <Button variant="ghost" size="icon">
                <Trash2 />
              </Button>
            </DialogDelete>
          </div>
        );
      },
    },
  ];

  const handleFAQ = async (formData) => {
    setLoading(true);
    setErrors({});
    try {
      if (FAQData?.id) {
        await axios.put(
          `${BaseUrlApi}/frequently-asked-questions/${FAQData?.id}`,
          formData
        );
        toast("FAQ saved", { status: "success" });
      } else {
        await axios.post(`${BaseUrlApi}/frequently-asked-questions`, formData);
        toast("FAQ added", { status: "success" });
      }
      await fetchFAQ();
      setOpen(false);
    } catch (error) {
      const msg = ErrorMessage(error);
      if (typeof msg === "string")
        toast("Error", { description: msg, status: "error" });
      setErrors(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="flex items-center gap-2 justify-between flex-wrap">
        <h1 className="text-2xl font-bold">FAQ</h1>
        <MainButton
          Icon={PlusCircle}
          title={"Add FAQ"}
          onClick={() => {
            setFAQData({});
            setOpen(true);
          }}
        />
        <FAQDialog
          open={open}
          onOpenChange={setOpen}
          FAQData={FAQData}
          saving={loading}
          setSaving={setLoading}
          onSave={handleFAQ}
          errors={errors}
        />
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Spin size="large" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={FAQ}
          title={"FAQ by question"}
          filterNames={["question"]}
        />
      )}
    </div>
  );
};

export default FAQPage;
